import {
  ChangeEvent,
  CSSProperties,
  HTMLAttributes,
  ReactNode,
  useState,
} from "react";
import { Icon } from "../Icon";
import "./../../globals.scss";
import styles from "./chip.module.scss";
import { LoadingSpinner } from "../LoadingSpinner";

interface ChipProps extends Omit<HTMLAttributes<HTMLSpanElement>, "onClick"> {
  label: ReactNode;
  active?: boolean;
  dark?: boolean;
  icon?: string;
  onClick?: (e: React.MouseEvent<HTMLSpanElement>, id: string) => void;
  onActionClick?: (id: string) => void;
  actionIcon?: string;
  actionStyles?: CSSProperties;
  inputField?: boolean;
  inputFieldDefaultNumber?: number;
  inputFieldConnectedNumber?: number;
  onNumberInputChange?: (value: number) => void;
  isLoading?: boolean;
}

export function Chip({
  label,
  id,
  active,
  dark,
  icon,
  className,
  onClick,
  onActionClick,
  actionIcon,
  actionStyles,
  inputField,
  inputFieldDefaultNumber,
  inputFieldConnectedNumber,
  onNumberInputChange,
  isLoading,
  ...props
}: ChipProps) {
  const [inputValue, setInputValue] = useState<number | string | undefined>(
    inputFieldDefaultNumber,
  );
  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(event.target.value);
    if (!isNaN(newValue)) {
      setInputValue(newValue);
      if (onNumberInputChange) {
        onNumberInputChange(newValue);
      }
    } else if (event.target.value === "") {
      setInputValue("");
    }
  };

  if (onClick) {
    return (
      <span
        {...props}
        id={id}
        role="button"
        tabIndex={0}
        aria-pressed={active}
        onClick={(e) => onClick(e, id as string)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(
              e as unknown as React.MouseEvent<HTMLSpanElement>,
              id as string,
            );
          }
        }}
        className={`${styles.chip} ${dark ? styles.dark : ""} ${
          styles.toggle
        } ${active ? styles.active : ""} ${className ? className : ""}`}
      >
        {icon && <Icon className={styles.leftIcon} name={icon} />}
        <span>{label}</span>
        {inputField && (
          <div className={styles.numberInputFiledContainer}>
            {inputFieldConnectedNumber && (
              <span>{`${inputFieldConnectedNumber} / `}</span>
            )}
            <input
              className={styles.numberInputField}
              type="number"
              min={inputFieldConnectedNumber ? inputFieldConnectedNumber : 0}
              value={inputValue}
              step={1}
              onChange={(e) => handleOnChange(e)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
        {isLoading ? (
          <div className={styles.icon} style={actionStyles}>
            <LoadingSpinner dark={dark} size={16} />
          </div>
        ) : active ? (
          <Icon
            className={styles.icon}
            style={actionStyles}
            name={actionIcon || "close"}
            onClick={
              onActionClick
                ? (e) => {
                    e.stopPropagation();
                    onActionClick(id as string);
                  }
                : undefined
            }
          />
        ) : (
          <Icon
            className={`${styles.icon} ${styles.add}`}
            style={actionStyles}
            name={actionIcon || "add"}
          />
        )}
      </span>
    );
  } else {
    return (
      <span
        {...props}
        className={`${styles.chip} ${dark ? styles.dark : ""} ${
          active ? styles.active : ""
        } ${className ? className : ""}`}
      >
        {icon && <Icon className={styles.leftIcon} name={icon} />}
        <span>{label}</span>
        {inputField && (
          <div className={styles.numberInputFiledContainer}>
            {(inputFieldConnectedNumber || inputFieldConnectedNumber === 0) && (
              <span>{`${inputFieldConnectedNumber} / `}</span>
            )}
            <input
              className={styles.numberInputField}
              type="number"
              min={inputFieldConnectedNumber ? inputFieldConnectedNumber : 1}
              value={inputValue}
              step={1}
              onChange={(e) => handleOnChange(e)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </span>
    );
  }
}
